import React from "react"
import { Box, Button, ButtonGroup, CircularProgress, Container, Grid, Paper, Typography } from "@material-ui/core"
import HourReportList from "./components/HourReportList/HourReportList"
import { HourReportForm } from "./components/HourReportForm/HourReportForm"
import { useHourReportForm } from "./hooks/use-hour-report-form"
import { useHourReportFormOptions } from "./hooks/use-form-options"
import { useHourReports } from "./hooks/use-hour-reports"
import { useAppContext } from "../app-context/use-app-context"
import { useAuthUserMembership } from "../memberships/hooks/use-auth-user-membership"
import { resolveCurrentMembership, resolveDefaultMembership, type ResolvedMembership } from "./resolve-membership"
import type { HourReportingWorkflowSettingsProps } from "./hooks/use-hour-reporting-workflow-settings"
import { getHourReportsHeaderText } from "./components/utils"
import ApprovedHoursTable from "../treegrid/approved-hours"
import { reportHoursColumnOrder, reportHoursVisibleColumns } from "../treegrid/approved-hours/column-settings"
import DateRangeSelector from "./components/DateRangeSelector/DateRangeSelector"
import { useDateRange } from "./components/DateRangeSelector/use-date-range"
import { useCalendarSettings } from "../hooks/use-calendar-settings"
import AddHourReportDialog from "./components/ApproveHours/AddHourReportDialog"
import HourReportCorrectionForm from "./components/ApproveHours/HourReportCorrectionForm"
import { useHourReportMutations } from "./hooks/use-hour-report-mutations"
import { useConfirmDialog } from "../components/ConfirmDialog"
import { useLocalizedFormat } from "../hooks/use-localized-format"
import DatePicker from "../components/DatePicker"
import { useReportingMethod } from "./hooks/use-reporting-method"
import { useTranslation } from "react-i18next"
import { useHourReportMembershipQuery } from "./hooks/use-hour-report-membership-query"

const HourReports = ({ workflowSettings }: HourReportingWorkflowSettingsProps) => {
  const { appContext } = useAppContext()
  const isOrganisationContext = appContext.mainContext?.type === "org"
  const {
    membership: authUserMembership,
    canReportOthersHoursAnywhere,
    canReportOthersHoursAsMember,
  } = useAuthUserMembership()
  const canReportOthersHours = Boolean(canReportOthersHoursAsMember || canReportOthersHoursAnywhere)
  const initialMembership = resolveDefaultMembership({
    subContext: appContext.subContext,
    authUserMembership,
    canReportOthersHours,
  })

  return (
    <HourReportsContent
      key={initialMembership?.id ?? "no-membership"}
      initialMembership={initialMembership}
      isUserContext={appContext.mainContext?.type === "user"}
      isOrganisationContext={isOrganisationContext}
      workflowSettings={workflowSettings}
    />
  )
}

const HourReportsContent = ({
  initialMembership,
  isUserContext,
  isOrganisationContext,
  workflowSettings,
}: {
  initialMembership: ResolvedMembership | null
  isUserContext: boolean
  isOrganisationContext: boolean
  workflowSettings: HourReportingWorkflowSettingsProps["workflowSettings"]
}) => {
  const localizedFormat = useLocalizedFormat()
  const { t } = useTranslation("hour-report")
  const { appContext } = useAppContext()
  const { fromDate, toDate, setFromDate, setToDate, setDateRange } = useDateRange()
  const { isSingleDay, setReportingMethod } = useReportingMethod({ fromDate, toDate, setDateRange })
  const { calendarSettings, isLoading: isLoadingCalendarSettings } = useCalendarSettings({
    mainContext: appContext.mainContext,
    skipAll: !appContext.mainContext,
  })
  const { formik, submitReport, saveAsDraft } = useHourReportForm({ initialMembershipId: initialMembership?.id })
  const { projectOptions, membershipOptions, meta: formOptionsMeta } = useHourReportFormOptions()
  const { selectedMembershipId, setSelectedMembershipId } = useHourReportMembershipQuery({
    defaultMembershipId: initialMembership?.id ?? null,
    membershipIds: membershipOptions.map(({ id }) => id),
    isLoading: formOptionsMeta.isLoading,
  })

  React.useEffect(() => {
    if (formik.values.membershipId !== selectedMembershipId) {
      formik.setFieldValue("membershipId", selectedMembershipId)
    }
  }, [formik, selectedMembershipId])

  const currentMembership = resolveCurrentMembership({
    membershipId: formik.values.membershipId ?? null,
    membershipOptions: membershipOptions ?? [],
    initialMembership,
  })
  const currentMembershipId = currentMembership?.id ?? null
  const currentMembershipName = currentMembership?.name ?? ""
  const {
    reports,
    totalReportedHours,
    meta: reportsMeta,
  } = useHourReports({
    fromDate,
    toDate: isSingleDay ? undefined : toDate,
    membershipId: currentMembershipId,
  })
  const headerText = getHourReportsHeaderText({
    fromDate,
    toDate: isSingleDay ? fromDate : toDate,
    isUserContext,
    membershipName: currentMembershipName,
    formatDate: localizedFormat,
    t,
  })

  React.useEffect(() => {
    if (!isSingleDay) return

    const selectedDate = localizedFormat(fromDate, "yyyy-MM-dd")
    if (formik.values.date !== selectedDate) formik.setFieldValue("date", selectedDate)
  }, [formik, fromDate, isSingleDay, localizedFormat])

  const handleSingleDayDateChange = (date: Date) => setDateRange({ fromDate: date, toDate: date })

  if (isLoadingCalendarSettings || formOptionsMeta.isLoading || reportsMeta.isLoading) {
    return <CircularProgress size={24} style={{ display: "block", margin: "16px auto" }} />
  }

  return (
    <Container maxWidth="lg" style={{ padding: 0 }}>
      <Paper variant="outlined" elevation={0} style={{ padding: 16, marginBottom: 16 }}>
        <Typography variant="h6">{headerText}</Typography>
        <Box mt={1.5}>
          <ButtonGroup size="small" aria-label={t("view.ariaLabel", { defaultValue: "Hour reporting view" })}>
            <Button
              variant={isSingleDay ? "contained" : "outlined"}
              color={isSingleDay ? "primary" : "default"}
              onClick={() => setReportingMethod("single-day")}
            >
              {t("view.singleDay", { defaultValue: "Report one day" })}
            </Button>
            <Button
              variant={!isSingleDay ? "contained" : "outlined"}
              color={!isSingleDay ? "primary" : "default"}
              onClick={() => setReportingMethod("multiple-days")}
            >
              {t("view.multipleDays", { defaultValue: "Report multiple days" })}
            </Button>
          </ButtonGroup>
        </Box>

        {isSingleDay ? (
          <Box mt={1.5} maxWidth={252}>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date | null) => date && handleSingleDayDateChange(date)}
              isClearable={false}
              dateFormat="MMM dd, yyyy"
              textFieldProps={{ label: t("view.date", { defaultValue: "Date" }), size: "small" }}
              calendarStartDay={calendarSettings.firstDayOfWeek}
              maxDate={new Date()}
            />
          </Box>
        ) : (
          <DateRangeSelector
            fromDate={fromDate}
            toDate={toDate}
            firstDayOfWeek={calendarSettings.firstDayOfWeek}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            onDateRangeChange={setDateRange}
            embedded
          />
        )}
      </Paper>

      {isSingleDay ? (
        <OneDayReports
          formik={formik}
          saveAsDraft={saveAsDraft}
          submitReport={submitReport}
          membershipId={currentMembershipId}
          memberDisplayName={currentMembershipName}
          reports={reports}
          totalReportedHours={totalReportedHours}
          workflowSettings={workflowSettings}
          projectOptions={projectOptions}
          membershipOptions={membershipOptions}
          onMembershipChange={setSelectedMembershipId}
        />
      ) : (
        <MultipleDayReports
          membershipId={currentMembershipId}
          reports={reports}
          fromDate={fromDate}
          toDate={toDate}
          dateFormat={calendarSettings.dateFnsDateFormat}
          dateSeparator={calendarSettings.dateFormatSpecs.dateSeparator}
          firstDayOfWeek={calendarSettings.firstDayOfWeek}
          gridInfo={headerText}
          workflowSettings={workflowSettings}
          isOrganisationContext={isOrganisationContext}
        />
      )}
    </Container>
  )
}

const OneDayReports = ({
  formik,
  saveAsDraft,
  submitReport,
  membershipId,
  memberDisplayName,
  reports,
  totalReportedHours,
  workflowSettings,
  projectOptions,
  membershipOptions,
  onMembershipChange,
}: OneDayReportsProps) => {
  return (
    <Grid container spacing={2} wrap="wrap">
      <Grid item xs={12} md={5} style={{ width: 440 }}>
        <HourReportForm
          formik={formik}
          saveAsDraft={saveAsDraft}
          submitReport={submitReport}
          workflowSettings={workflowSettings}
          projectOptions={projectOptions}
          membershipOptions={membershipOptions}
          onMembershipChange={onMembershipChange}
          showDayPicker={false}
        />
      </Grid>
      <Grid item xs={12} md={7} style={{ flex: 1 }}>
        <HourReportList
          reports={reports}
          totalReportedHours={totalReportedHours}
          reportedForName={memberDisplayName}
          workflowSettings={workflowSettings}
        />
      </Grid>
    </Grid>
  )
}

const MultipleDayReports = ({
  membershipId,
  reports,
  workflowSettings,
  isOrganisationContext,
  fromDate,
  toDate,
  dateFormat,
  dateSeparator,
  firstDayOfWeek,
  gridInfo,
}: MultipleDayReportsProps) => {
  const { t } = useTranslation("hour-report")
  const [addHourReportDate, setAddHourReportDate] = React.useState<Date | null>(null)
  const [editingReportId, setEditingReportId] = React.useState<string | null>(null)
  const editingReport = reports.find((report) => report.id === editingReportId)
  const { deleteHourReport } = useHourReportMutations()
  const { confirm: handleDeleteHourReport } = useConfirmDialog({
    title: t("dialogs.deleteTitle", { defaultValue: "Delete Hour Report" }),
    text: t("dialogs.deleteText", { defaultValue: "Are you sure you want to delete this hour report?" }),
    primaryActionButtonLabel: t("actions.delete", { defaultValue: "Delete" }),
    onConfirm: (id) => {
      const report = reports.find((item) => item.id === id)
      if (!report?.editableAs) return
      return deleteHourReport(id, report.editableAs)
    },
  })
  return (
    <Box>
      <ApprovedHoursTable
        reports={reports}
        dayAudits={[]}
        selectedMembershipId={membershipId ?? undefined}
        enableAddingReports={true}
        dateFormat={dateFormat}
        dateSeparator={dateSeparator}
        firstDayOfWeek={firstDayOfWeek}
        gridInfo={gridInfo}
        fromDate={fromDate}
        toDate={toDate}
        onAddHourReport={setAddHourReportDate}
        onEditHourReport={setEditingReportId}
        onDeleteHourReport={handleDeleteHourReport}
        workflowSettings={workflowSettings}
        isOrganisationContext={isOrganisationContext}
        columnOrder={reportHoursColumnOrder}
        visibleColumns={reportHoursVisibleColumns}
      />
      {addHourReportDate ? (
        <AddHourReportDialog
          date={addHourReportDate}
          selectedMembershipId={membershipId}
          workflowSettings={workflowSettings}
          onClose={() => setAddHourReportDate(null)}
        />
      ) : null}
      <HourReportCorrectionForm
        report={editingReport}
        editableAs={editingReport?.editableAs ?? null}
        setEditingReportId={setEditingReportId}
      />
    </Box>
  )
}

type OneDayReportsProps = {
  formik: ReturnType<typeof useHourReportForm>["formik"]
  saveAsDraft: () => void
  submitReport: () => void
  membershipId: string | null
  memberDisplayName: string
  reports: ReturnType<typeof useHourReports>["reports"]
  totalReportedHours: ReturnType<typeof useHourReports>["totalReportedHours"]
  workflowSettings: HourReportingWorkflowSettingsProps["workflowSettings"]
  projectOptions: ReturnType<typeof useHourReportFormOptions>["projectOptions"]
  membershipOptions: ReturnType<typeof useHourReportFormOptions>["membershipOptions"]
  onMembershipChange: (membershipId: string | null) => void
}

type MultipleDayReportsProps = {
  membershipId: string | null
  reports: ReturnType<typeof useHourReports>["reports"]
  workflowSettings: HourReportingWorkflowSettingsProps["workflowSettings"]
  isOrganisationContext: boolean
  fromDate: Date
  toDate: Date
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
}

export default HourReports
